import { BaseDevice } from '../../../runtime/BaseDevice'
import type { HidChannel } from '../../../channels/hid/HidChannel'
import { DeviceManifest, type InputReportCapability } from '../../../types/manifest'
import { ReportEvents } from '../../../events/ReportEvents'
import { NoticeEvents } from '../../../events/NoticeEvents'
import { VTNrf54LDPI } from '../../../chips/nordic-nrf54l/feature-impls/mouse/dpi'
import { NordicNrf54LConfig } from '../../../chips/nordic-nrf54l/feature-impls/mouse/config'
import { VTNrf54LKeys } from '../../../chips/nordic-nrf54l/feature-impls/mouse/mouse-keys'
import { VTNrf54LPerfCfg } from '../../../chips/nordic-nrf54l/feature-impls/mouse/mouse-performance'
import { VTNrf54LFirmware } from '../../../chips/nordic-nrf54l/feature-impls/mouse/firmware'
import { VTNrf54LPairing } from '../../../chips/nordic-nrf54l/feature-impls/mouse/pairing'
import { ReportBackfill } from '../../../cache/ReportBackfill'
import type { ReportBackfillRule } from '../../../cache/types'
import { NRF54L_MOUSE_REPORT_RULES } from '../../../chips/nordic-nrf54l/protocols/report-rules'
import { parseNrf54LMouseUserCustomData } from './user-custom-report'
import { toAbsoluteConfigIndex } from '../../../utils/configSlots'

/** VT_nrf54L 系列鼠标设备连接协议值常量 */
const CONN_WIRED = 0xff as const
const CONN_WIRELESS = 0xa5 as const

/**
 * VT 系列鼠标设备驱动实现。
 * 该设备形态使用 Nordic nRF54L 协议/芯片能力，但目录按"鼠标设备族"归类。
 *
 * 这是一个 "容器类"，负责组装各个功能模块
 */
export class VTNrf54LMouseDevice extends BaseDevice {
  /** Phase 4: 上报数据回填引擎实例,用于 close/disconnect 时调用 stop() */
  private readonly reportBackfill: ReportBackfill

  constructor(channel: HidChannel, manifest: DeviceManifest) {
    super(channel, manifest)

    // 自动检测连接类型
    this.detectConnectionType()

    // 组装具体的功能实现
    // 将 Feature 实例直接挂载到 features 容器中
    this.features.dpi = new VTNrf54LDPI(this)
    this.features.config = new NordicNrf54LConfig(this)
    this.features.mouseKeys = new VTNrf54LKeys(this)
    this.features.mousePerfCfg = new VTNrf54LPerfCfg(this)
    this.features.firmware = new VTNrf54LFirmware(this)
    this.features.pairing = new VTNrf54LPairing(this)

    // Phase 4: 启动上报回填,让高频字段(CPI档位/回报率/扫描率/cfgSel)通过 report 自动进缓存
    this.reportBackfill = new ReportBackfill(
      this,
      NRF54L_MOUSE_REPORT_RULES as ReportBackfillRule[],
    )
    this.reportBackfill.start()
  }

  /**
   * 检测连接类型 (有线/无线)
   * 策略：
   * 1. 检查 Manifest 定义 (假设第二个 PID 为无线)
   * 2. 检查设备名称是否包含 "Receiver"
   */
  private detectConnectionType() {
    const info = this.channel.deviceInfo
    if (!info) return

    const pids = Array.isArray(this.manifest.hardware.productId)
      ? this.manifest.hardware.productId
      : [this.manifest.hardware.productId]

    let isWireless = false

    // 策略 1: 如果 PID 是列表中的第二个，认为是无线
    if (pids.length > 1 && info.productId === pids[1]) {
      isWireless = true
    }
    // 策略 2: 名称包含 "Receiver" 或 "Dongle"
    else if (info.productName && /Receiver|Dongle/i.test(info.productName)) {
      isWireless = true
    }

    if (isWireless) {
      this.connectionType = CONN_WIRELESS
      console.log(
        `[VTNrf54LMouseDevice] Detected Wireless Mode (PID: 0x${info.productId.toString(16)})`,
      )
    } else {
      this.connectionType = CONN_WIRED
      console.log(
        `[VTNrf54LMouseDevice] Detected Wired Mode (PID: 0x${info.productId.toString(16)})`,
      )
    }
  }

  private getInputReportType(): string | undefined {
    const reportCapability = this.manifest.capabilities.find(
      (item): item is InputReportCapability => item.type === 'input-report',
    )
    return reportCapability?.config.type
  }

  protected onConnected(): void {
    console.log(`[VTNrf54LMouseDevice] Connected: ${this.manifest.model}`)
    // 网吧槽位空名由 getAllConfigs → ensureCybercafeConfigSlotNames 懒补
    void this.features.config
      ?.getAllConfigs()
      .then(() => this.features.config?.getCurrentConfig())
      .then((config) => {
        if (config) this.currentConfig = config
      })
      .catch((err) => {
        console.error('[VTNrf54LMouseDevice] Failed to load configs after connect:', err)
      })
    this.features.dpi?.getCurrentGear().catch((err) => {
      console.error('[VTNrf54LMouseDevice] Failed to get current DPI gear:', err)
    })
  }

  protected onDisconnected(): void {
    this.reportBackfill.stop()
  }

  async checkConnect(): Promise<boolean> {
    try {
      const isConnected = this.channel?.isConnected
      if (!isConnected) {
        return false
      }

      await this.features.firmware?.getFirmwareVersion()
      return true
    } catch (_error) {
      console.error('[VTNrf54LMouseDevice] Failed to get firmware version', _error)
      return false
    }
  }

  protected async handleReport(reportId: number, data: DataView): Promise<void> {
    // console.log(`[VTNrf54LMouseDevice] Received report ${reportId}, length: ${data.byteLength}`, data)
    if (reportId === 0x07) {
      const userData = parseNrf54LMouseUserCustomData(
        new Uint8Array(data.buffer, data.byteOffset, data.byteLength),
        this.getInputReportType(),
      )
      if (!userData) return
      // console.log('userData', userData, data.buffer)
      // 如果上报了cfg配置变更，那么其他变更这次上报就不用发出消息提醒了
      let isReportCfg = false
      // 设备运行态与业务事件统一使用绝对槽位 ID。不能固定 +4，因为槽位可被多角色共享。
      // 短包未携带 cfgSel 时保持当前槽位，不伪造配置切换。
      const configIndex =
        userData.cfgSel == null
          ? this.currentConfig.index
          : toAbsoluteConfigIndex(userData.cfgSel, this.manifest.capabilities, this.configSlotRoles)
      // MouseUserData 同时保留固件原始 cfgSel 和绝对 cfgSlot，供 ReportBackfill 分别回填。
      const reportUserData =
        userData.cfgSel == null || configIndex === userData.cfgSel
          ? userData
          : { ...userData, cfgSlot: configIndex }
      this.emit(ReportEvents.MouseUserData, reportUserData)
      const appUserData =
        userData.cfgSel == null || configIndex === userData.cfgSel
          ? userData
          : { ...reportUserData, cfgSel: configIndex }
      // 同步当前配置
      if (this.currentConfig.index !== configIndex) {
        this.emit(ReportEvents.CfgChanged, appUserData)
        const currentConfig = this.configList.find((config) => config.index === configIndex)
        if (currentConfig) {
          this.currentConfig = currentConfig
        } else {
          const config = this.features.config
          if (config) {
            const configs = await config.getAllConfigs()
            const matchedConfig = configs.find((cfg) => cfg.index === configIndex)
            if (matchedConfig) {
              this.currentConfig = matchedConfig
            }
          }
        }
        this.emit(NoticeEvents.CfgChanged, this.currentConfig)
        isReportCfg = true
      }
      // 电量或充电状态有变化时才抛出事件
      const prevData = this.userCustomData as typeof userData | undefined
      if (
        prevData?.batPercent !== userData.batPercent ||
        prevData?.batStatus !== userData.batStatus
      ) {
        this.emit(ReportEvents.Battery, {
          batPercent: userData.batPercent,
          batStatus: userData.batStatus,
        })
      }

      // 设备切换dpi抛出事件
      if (prevData?.cpiLev !== userData?.cpiLev) {
        const dpiInfo = {
          cpiLev: userData.cpiLev,
          xCpiVal: userData.xCpiVal,
          yCpiVal: userData.yCpiVal,
        }
        this.emit(ReportEvents.DpiGearChanged, dpiInfo)
        if (prevData !== undefined && !isReportCfg) {
          this.emit(NoticeEvents.DpiChanged, dpiInfo)
        }
      }

      // 更新用户自定义数据
      this.userCustomData = appUserData
      // DPI 上报时失效当前档位缓存,确保下次读取拿到最新值(tag-mapping §1 场景B)
      if (prevData?.cpiLev !== userData?.cpiLev) {
        this.cache.invalidateByTag(`dpi:current:${this.currentConfig.index}`)
      }
      // 回报率变化：失效缓存 → 发射 report 事件（供 Web UI 更新） → 发射 notice（首次忽略，与 DPI 一致）
      if (prevData?.rpt !== userData.rpt) {
        this.cache.invalidateByTag(`cfg:${this.currentConfig.index}`)
        const config = {
          reportRate: userData.rpt,
          sensorMode: userData.sensorMode,
        }
        this.emit(ReportEvents.ReportRateChanged, config)
        if (prevData !== undefined && !isReportCfg) {
          this.emit(NoticeEvents.ReportRateChanged, config)
        }
      }
      // 按键扫描率变化：失效缓存 → 发射 report 事件 → 发射 notice
      if (prevData?.kRate !== userData.kRate) {
        this.cache.invalidateByTag(`cfg:${this.currentConfig.index}`)
        this.emit(ReportEvents.ScanRateChanged, userData.kRate)
        if (prevData !== undefined && !isReportCfg) {
          this.emit(NoticeEvents.ScanRateChanged, userData.kRate)
        }
      }
      // 模式切换判断
      const conProtocol = userData.conProtocol
      const lineToWireless = conProtocol === 0 && this.connectionType === CONN_WIRED
      const wirelessToLine = conProtocol === 2 && this.connectionType === CONN_WIRELESS
      if (lineToWireless || wirelessToLine) {
        console.log(
          `[VTNrf54LMouseDevice] Connection type changed: ${lineToWireless ? 'Line -> Wireless' : 'Wireless -> Line'}`,
        )
      }
    }
  }
}
